import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { invalidateCachedFetch, useAuth } from '@structural-climbing/auth'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  SUBSCRIPTION_STATUS_STYLES,
  holdsPlan,
  type SubscriptionStatus,
  DateField,
} from '@structural-climbing/ui'
import { PENDING_DOCUMENTS_CHANGED } from '../legal/usePendingDocuments'
import { ChildAccountControl, type ChildAccount } from './ChildAccountControl'
import { NextPlanLine } from '../membership/NextPlanLine'
import { MY_CHILDREN_CACHE_PREFIX } from './useMyChildren'
import { personColor } from './personColor'
import { SurfaceCard } from '../../design/SurfaceCard'

interface ChildSubscription {
  id: string
  entries_remaining: number | null
  starts_at: string | null
  expires_at: string | null
  activated_at: string | null
  frozen_until?: string | null
  status: string
  /** Authoritative status — accounts for expiry the DB `status` column hasn't caught up to. */
  effective_status: SubscriptionStatus
  gym_plans: { name: string; plan_type: string }
  /** Set on a renewal: the gym's renewal discount, when it was given. */
  renewal_discount_percent?: number | null
}

interface Child {
  id: string
  first_name: string
  last_name: string | null
  birth_date: string | null
  gender?: string | null
  active_subscription: ChildSubscription | null | undefined
  /** The renewal bought to follow the child's plan. */
  upcoming_subscription?: ChildSubscription | null
  /** The child's own login. Absent on a server that does not offer them. */
  account?: ChildAccount | null
}

interface Props {
  gymId: string | null
  childCheckIns: Record<string, string>
  gymTimezone: string | null
}

/**
 * The child's gender, chosen by the parent. Required.
 *
 * Competitions are usually split by gender, and a child with a login of their
 * own cannot set it: what the parent picks here is what a competition reads.
 * Two buttons rather than a dropdown, since there are two answers and one tap
 * should be enough.
 */
function ChildGenderChoice({
  value,
  onChange,
}: {
  value: string
  onChange: (gender: 'male' | 'female') => void
}) {
  const { t } = useTranslation()
  const options = [
    { key: 'male' as const, label: t('profile.genderMale') },
    { key: 'female' as const, label: t('profile.genderFemale') },
  ]
  return (
    <div role="group" aria-label={t('family.genderLabel')}>
      <p className="mb-1 text-sm font-medium">{t('family.genderLabel')}</p>
      <div className="flex gap-2">
        {options.map((option) => (
          <Button
            key={option.key}
            size="sm"
            className="flex-1"
            variant={value === option.key ? 'solid' : 'flat'}
            color={value === option.key ? 'primary' : 'default'}
            aria-pressed={value === option.key}
            onClick={() => onChange(option.key)}
          >
            {option.label}
          </Button>
        ))}
      </div>
      <p className="mt-2 text-xs text-foreground/50">{t('family.genderHint')}</p>
    </div>
  )
}

/** Default birth date for a new child: today minus 10 years, as `YYYY-MM-DD`. */
function defaultChildBirthDate() {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 10)
  return d.toISOString().slice(0, 10)
}

export function FamilySection({ gymId, childCheckIns, gymTimezone }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()

  const STATUS_LABELS: Record<SubscriptionStatus, string> = {
    active: t('membership.statusActive'),
    scheduled: t('membership.statusScheduled'),
    frozen: t('membership.statusFrozen'),
    expired: t('membership.statusExpired'),
    depleted: t('membership.statusDepleted'),
    cancelled: t('membership.statusCancelled'),
  }

  const apiUrl = import.meta.env.VITE_API_URL as string

  const [children, setChildren] = useState<Child[]>([])
  // Whether the server offers child logins at all. Off until it says otherwise,
  // so the control never appears against an API that would refuse it.
  const [invitesEnabled, setInvitesEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    birth_date: defaultChildBirthDate(),
    gender: '',
  })
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Edit modal
  const [editChild, setEditChild] = useState<Child | null>(null)
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
    gender: '',
  })
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Delete confirmation modal
  const [deleteTarget, setDeleteTarget] = useState<Child | null>(null)
  const [deleteConfirmError, setDeleteConfirmError] = useState<string | null>(null)

  async function loadChildren() {
    try {
      const token = await getAccessTokenSilently()
      // Scope each child's subscription/QR to the gym currently being viewed.
      const url = gymId
        ? `${apiUrl}/my-children?gym_id=${encodeURIComponent(gymId)}`
        : `${apiUrl}/my-children`
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = (await res.json()) as { children: Child[]; invites_enabled?: boolean }
        setChildren(data.children)
        setInvitesEnabled(data.invites_enabled === true)
        // Home shows these children too, from its own cached copy.
        invalidateCachedFetch(MY_CHILDREN_CACHE_PREFIX)
      }
    } catch {
      // non-critical — just don't show children
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadChildren()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymId])

  async function handleAddChild() {
    if (!form.first_name.trim()) {
      setFormError(t('family.firstNameRequired'))
      return
    }
    if (!form.last_name.trim()) {
      setFormError(t('family.lastNameRequired'))
      return
    }
    if (!form.birth_date) {
      setFormError(t('family.birthDateRequired'))
      return
    }
    if (!form.gender) {
      setFormError(t('family.genderRequired'))
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/children`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          birth_date: form.birth_date,
          gender: form.gender || null,
        }),
      })
      const data = (await res.json()) as { error?: string }
      if (!res.ok) {
        setFormError(data.error ?? t('family.errorAdd'))
        return
      }
      setAddOpen(false)
      setForm({ first_name: '', last_name: '', birth_date: defaultChildBirthDate(), gender: '' })
      void loadChildren()
      // The gym may ask a parent to sign a document for each child; the gate
      // picks the new child up and opens straight away.
      window.dispatchEvent(new Event(PENDING_DOCUMENTS_CHANGED))
    } finally {
      setSaving(false)
    }
  }

  function handleOpenEdit(child: Child) {
    setEditChild(child)
    setEditForm({
      first_name: child.first_name,
      last_name: child.last_name ?? '',
      birth_date: child.birth_date ?? '',
      gender: child.gender ?? '',
    })
    setEditError(null)
  }

  async function handleUpdateChildSubmit() {
    if (!editChild) return
    if (!editForm.first_name.trim()) {
      setEditError(t('family.firstNameRequired'))
      return
    }
    if (!editForm.last_name.trim()) {
      setEditError(t('family.lastNameRequired'))
      return
    }
    if (!editForm.birth_date) {
      setEditError(t('family.birthDateRequired'))
      return
    }
    if (!editForm.gender) {
      setEditError(t('family.genderRequired'))
      return
    }
    setEditSaving(true)
    setEditError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/children/${editChild.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: editForm.first_name.trim(),
          last_name: editForm.last_name.trim(),
          birth_date: editForm.birth_date,
          gender: editForm.gender || null,
        }),
      })
      const data = (await res.json()) as { error?: string; resignRequired?: boolean }
      if (!res.ok) {
        setEditError(data.error ?? t('family.errorEdit'))
        return
      }
      setEditChild(null)
      void loadChildren()
      // The child's name is on the documents signed for them, so a new name
      // brings those back, and the gate opens straight away.
      if (data.resignRequired) window.dispatchEvent(new Event(PENDING_DOCUMENTS_CHANGED))
    } finally {
      setEditSaving(false)
    }
  }

  function handleDeleteChild(child: Child) {
    setDeleteTarget(child)
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    setDeleteConfirmError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/children/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const data = (await res.json()) as { error?: string }
        setDeleteConfirmError(
          res.status === 409
            ? t('family.removeBlockedBySubscription')
            : (data.error ?? t('family.removeError')),
        )
        return
      }
      setDeleteTarget(null)
      void loadChildren()
    } finally {
      setDeletingId(null)
    }
  }

  // Only show the section inside a gym, or when there are children to list
  if (!gymId && !loading && children.length === 0) return null

  return (
    <>
      <section className="flex flex-col gap-3" aria-label={t('family.title')}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold leading-tight">{t('family.title')}</h2>
            <p className="text-sm text-foreground/50">{t('family.subtitle')}</p>
          </div>
          {gymId && (
            <Button
              size="sm"
              className="shrink-0 bg-[var(--gym-primary)] font-semibold text-[var(--gym-on-primary)]"
              onClick={() => setAddOpen(true)}
            >
              {t('family.addChild')}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-4">
            <Spinner size="sm" color="primary" />
          </div>
        ) : children.length === 0 ? (
          <SurfaceCard className="px-4 py-6">
            <p className="text-center text-sm text-foreground/50">{t('family.empty')}</p>
          </SurfaceCard>
        ) : (
          children.map((child) => {
            const name = [child.first_name, child.last_name].filter(Boolean).join(' ')
            const sub = child.active_subscription
            // A plan that is still running, or one starting later, blocks removal:
            // either way it is the child's plan.
            const holdsSub = holdsPlan(sub?.effective_status)
            const inside = childCheckIns[child.id]
            const isSubActive = sub?.effective_status === 'active'
            const meta = [
              child.birth_date ? new Date(child.birth_date).toLocaleDateString() : null,
              sub ? sub.gym_plans.name : t('family.noPlanShort'),
            ]
              .filter(Boolean)
              .join(' · ')
            const dates = sub?.expires_at
              ? t(
                  sub.effective_status === 'expired'
                    ? 'membership.expiredOn'
                    : 'membership.validUntil',
                  {
                    date: new Date(sub.expires_at).toLocaleDateString(),
                  },
                )
              : null
            const tz = { timeZone: gymTimezone ?? undefined }
            // Why a plan the child holds does not open the door today.
            const why =
              !sub || isSubActive
                ? null
                : sub.effective_status === 'scheduled' && sub.starts_at
                  ? t('membership.notStartedYet', {
                      date: new Date(sub.starts_at).toLocaleDateString(undefined, tz),
                    })
                  : sub.effective_status === 'frozen' && sub.frozen_until
                    ? t('membership.frozenNotice', {
                        date: new Date(sub.frozen_until).toLocaleDateString(undefined, tz),
                      })
                    : t('membership.contactGymPlan')

            const removeButton = (
              <Button
                size="sm"
                variant="bordered"
                className="border-[var(--gym-line)] font-semibold text-danger"
                isLoading={deletingId === child.id}
                isDisabled={deletingId !== null || holdsSub}
                onClick={() => handleDeleteChild(child)}
              >
                {t('common.remove')}
              </Button>
            )

            return (
              <SurfaceCard key={child.id} className="flex flex-col gap-3 p-4">
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-bold text-white"
                    style={{ backgroundColor: personColor(child.id) }}
                  >
                    {child.first_name.charAt(0).toUpperCase()}
                    {inside && (
                      <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-content1 bg-success" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-bold leading-tight">{name}</p>
                    <p className="mt-0.5 text-sm text-foreground/50">{meta}</p>
                    {sub && (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground/50">
                        {!isSubActive && (
                          <span
                            className={`rounded-full px-2 py-0.5 font-semibold ${SUBSCRIPTION_STATUS_STYLES[sub.effective_status] ?? ''}`}
                          >
                            {STATUS_LABELS[sub.effective_status]}
                          </span>
                        )}
                        {isSubActive && sub.entries_remaining !== null && (
                          <span>
                            {t('family.entriesRemaining', { count: sub.entries_remaining })}
                          </span>
                        )}
                        {dates && <span>{dates}</span>}
                      </div>
                    )}
                    {why && <p className="mt-1 text-xs text-foreground/45">{why}</p>}
                    {child.upcoming_subscription?.starts_at && (
                      <div className="mt-1">
                        <NextPlanLine
                          planName={child.upcoming_subscription.gym_plans.name}
                          startsAt={child.upcoming_subscription.starts_at}
                          discountPercent={child.upcoming_subscription.renewal_discount_percent}
                          gymTimezone={gymTimezone}
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="bordered"
                    className="shrink-0 border-[var(--gym-line)] font-semibold"
                    aria-label={t('family.editChildLabel', { name: child.first_name })}
                    isDisabled={deletingId !== null}
                    onClick={() => handleOpenEdit(child)}
                  >
                    {t('family.edit')}
                  </Button>
                </div>

                {invitesEnabled ? (
                  <ChildAccountControl
                    childId={child.id}
                    firstName={child.first_name}
                    account={child.account ?? null}
                    hasGender={Boolean(child.gender)}
                    onChanged={() => void loadChildren()}
                    extraAction={removeButton}
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">{removeButton}</div>
                )}
                {holdsSub && (
                  <p className="-mt-1 text-xs text-foreground/45">
                    {t('family.removeBlockedHint')}
                  </p>
                )}
              </SurfaceCard>
            )
          })
        )}
      </section>

      {/* Add child modal */}
      <Modal
        isOpen={addOpen}
        onOpenChange={(open) => {
          if (!open) setAddOpen(false)
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('family.addChildTitle')}</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-3">
                  <Input
                    label={t('family.firstNameLabel')}
                    placeholder={t('family.namePlaceholder')}
                    value={form.first_name}
                    onValueChange={(v) => setForm((f) => ({ ...f, first_name: v }))}
                    autoFocus
                  />
                  <Input
                    label={t('family.lastNameLabel')}
                    placeholder={t('family.lastNamePlaceholder')}
                    value={form.last_name}
                    onValueChange={(v) => setForm((f) => ({ ...f, last_name: v }))}
                  />
                  <DateField
                    label={t('family.birthDateLabelRequired')}
                    value={form.birth_date}
                    onValueChange={(v) => setForm((f) => ({ ...f, birth_date: v }))}
                  />
                  <ChildGenderChoice
                    value={form.gender}
                    onChange={(gender) => setForm((f) => ({ ...f, gender }))}
                  />
                  {formError && <p className="text-sm text-danger">{formError}</p>}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onClick={() => setAddOpen(false)} isDisabled={saving}>
                  {t('common.cancel')}
                </Button>
                <Button
                  color="primary"
                  onClick={() => void handleAddChild()}
                  isLoading={saving}
                  isDisabled={
                    !form.first_name.trim() ||
                    !form.last_name.trim() ||
                    !form.birth_date ||
                    !form.gender
                  }
                >
                  {t('common.add')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Edit child modal */}
      <Modal
        isOpen={editChild !== null}
        onOpenChange={(open) => {
          if (!open) setEditChild(null)
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('family.editChildTitle')}</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-3">
                  <Input
                    label={t('family.firstNameLabel')}
                    placeholder={t('family.namePlaceholder')}
                    value={editForm.first_name}
                    onValueChange={(v) => setEditForm((f) => ({ ...f, first_name: v }))}
                    autoFocus
                  />
                  <Input
                    label={t('family.lastNameLabel')}
                    placeholder={t('family.lastNamePlaceholder')}
                    value={editForm.last_name}
                    onValueChange={(v) => setEditForm((f) => ({ ...f, last_name: v }))}
                  />
                  <DateField
                    label={t('family.birthDateLabelRequired')}
                    value={editForm.birth_date}
                    onValueChange={(v) => setEditForm((f) => ({ ...f, birth_date: v }))}
                  />
                  <ChildGenderChoice
                    value={editForm.gender}
                    onChange={(gender) => setEditForm((f) => ({ ...f, gender }))}
                  />
                  {editError && <p className="text-sm text-danger">{editError}</p>}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onClick={() => setEditChild(null)} isDisabled={editSaving}>
                  {t('common.cancel')}
                </Button>
                <Button
                  color="primary"
                  onClick={() => void handleUpdateChildSubmit()}
                  isLoading={editSaving}
                  isDisabled={
                    !editForm.first_name.trim() ||
                    !editForm.last_name.trim() ||
                    !editForm.birth_date ||
                    !editForm.gender
                  }
                >
                  {t('common.save')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Delete confirmation — custom div to avoid React 19 + HeroUI Button.onPress bug */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[var(--z-overlay)] flex items-center justify-center bg-black/50 px-6">
          <div className="w-full max-w-sm rounded-2xl bg-content1 p-6 shadow-xl">
            <p className="mb-2 text-base font-semibold">{t('family.removeConfirmTitle')}</p>
            <p className="mb-6 text-sm text-foreground/60">
              {t('family.removeConfirm', { name: deleteTarget.first_name })}
            </p>
            {deleteConfirmError && <p className="mb-4 text-sm text-danger">{deleteConfirmError}</p>}
            <div className="flex gap-3">
              <Button
                variant="flat"
                className="flex-1"
                isDisabled={deletingId !== null}
                onClick={() => {
                  setDeleteTarget(null)
                  setDeleteConfirmError(null)
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="danger"
                className="flex-1"
                isLoading={deletingId !== null}
                onClick={() => void handleConfirmDelete()}
              >
                {t('family.removeConfirmButton')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
